import React, { useRef, useEffect, useState } from 'react';
import { X, Mail, Heart, Copy, Check } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const email = 'rumiaiengine@gmail.com';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        ref={modalRef}
        className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 max-w-md w-full relative"
      >
        <button
          onClick={onClose}
          className="absolute -right-2 -top-2 w-12 h-12 flex items-center justify-center rounded-xl hover:bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-all duration-300 backdrop-blur-sm"
          aria-label="Close modal"
        >
          <div className="bg-slate-900/90 w-8 h-8 rounded-lg flex items-center justify-center">
            <X className="w-5 h-5" />
          </div>
        </button>

        <div className="mb-6">
          <h2 className="text-2xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">
            Contact Us
          </h2>
          <p className="text-sm bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            We'd love to hear from you
          </p>
        </div>

        <div className="space-y-6">
          <div className="glass-panel p-4 rounded-xl flex items-center justify-between group">
            <div className="flex items-center space-x-3 flex-1">
              <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span 
                className="text-slate-200 select-all cursor-text"
                role="textbox"
                tabIndex={0}
              >
                {email}
              </span>
            </div>
            <button
              onClick={handleCopyEmail}
              className={`p-2 rounded-lg transition-all duration-300 transform active:scale-95 ml-2 ${
                copied
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'hover:bg-slate-800/50 text-slate-400 hover:text-slate-200'
              }`}
              title={copied ? 'Copied!' : 'Copy email address'}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          <div className="glass-panel-glow p-6 rounded-xl space-y-4">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Heart className="w-6 h-6 text-purple-400" />
              <h3 className="text-lg font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Thank You
              </h3>
            </div>
            
            <p className="text-slate-300 leading-relaxed text-center">
              Ask Rumi was created with love and dedication, inspired by my daughter Kimora, who gave it its name. It represents our commitment to making learning engaging and accessible for everyone.
            </p>
            
            <p className="text-slate-300 leading-relaxed text-center">
              We hope this AI companion helps you on your educational journey. Remember, every question you ask is a step toward greater understanding. Keep learning, stay curious, and never stop exploring!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactModal;